import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['places'];
const TROIA_CENTER = { lat: 38.4925, lng: -8.8975 };

const timeAgo = (iso, language) => {
  if (!iso) return language === 'pt' ? 'sem dados' : 'no data';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return language === 'pt' ? `há ${secs}s` : `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return language === 'pt' ? `há ${mins} min` : `${mins} min ago`;
  return language === 'pt' ? `há ${Math.floor(mins / 60)}h` : `${Math.floor(mins / 60)}h ago`;
};

export const DriverLiveMap = ({ token, driverId, open, onClose, language }) => {
  const [drivers, setDrivers] = useState([]);
  const [selectedId, setSelectedId] = useState(driverId);
  const mapRef = useRef(null);
  const centeredRef = useRef(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => { setSelectedId(driverId); centeredRef.current = false; }, [driverId, open]);

  // Live polling every 5s while the dialog is open
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/available-drivers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok && alive) setDrivers(await r.json());
      } catch (e) { console.debug('driver poll failed', e); }
    };
    load();
    const t = setInterval(load, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [open, token]);

  const located = drivers.filter((d) => d.latitude != null && d.longitude != null);
  const selected = located.find((d) => d.driver_id === selectedId) || null;

  // Center on the clicked driver once we have their position
  useEffect(() => {
    if (selected && mapRef.current && !centeredRef.current) {
      mapRef.current.panTo({ lat: selected.latitude, lng: selected.longitude });
      mapRef.current.setZoom(15);
      centeredRef.current = true;
    }
  }, [selected]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="driver-live-map-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#004E64]">
            {language === 'pt' ? 'Localização em Tempo Real' : 'Live Location'}
            <span className="flex items-center gap-1 text-xs font-normal text-green-600">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              {language === 'pt' ? 'ao vivo (3s)' : 'live (3s)'}
            </span>
          </DialogTitle>
          <DialogDescription>
            {language === 'pt' ? 'Posição dos entregadores atualizada automaticamente' : 'Driver positions update automatically'}
          </DialogDescription>
        </DialogHeader>
        {!isLoaded ? (
          <p className="text-sm text-[#9FA2B4] py-10 text-center">A carregar mapa...</p>
        ) : (
          <div className="rounded-xl overflow-hidden border">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '460px' }}
              center={selected ? { lat: selected.latitude, lng: selected.longitude } : TROIA_CENTER}
              zoom={14}
              onLoad={(m) => { mapRef.current = m; }}
              options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
            >
              {located.map((d) => (
                <Marker
                  key={d.driver_id}
                  position={{ lat: d.latitude, lng: d.longitude }}
                  onClick={() => setSelectedId(d.driver_id)}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: d.driver_id === selectedId ? 11 : 8,
                    fillColor: d.driver_id === selectedId ? '#FF6B35' : '#2EC4B6',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                  }}
                  label={{ text: (d.name || 'D').charAt(0), color: '#fff', fontSize: '11px', fontWeight: 'bold' }}
                />
              ))}
              {selected && (
                <InfoWindow
                  position={{ lat: selected.latitude, lng: selected.longitude }}
                  options={{ pixelOffset: new window.google.maps.Size(0, -14) }}
                >
                  <div style={{ fontFamily: 'inherit', minWidth: 140, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selected.photo_url && (
                      <img src={selected.photo_url} alt={selected.name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                    )}
                    <div>
                    <p style={{ fontWeight: 700, margin: 0, color: '#004E64' }}>{selected.name}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#666' }}>
                      {selected.vehicle_type || ''}{selected.vehicle_type ? ' · ' : ''}
                      {language === 'pt' ? 'Atualizado' : 'Updated'} {timeAgo(selected.location_updated, language)}
                    </p>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </div>
        )}
        {selectedId && !selected && drivers.length > 0 && (
          <p className="text-sm text-yellow-600">
            {language === 'pt' ? 'Este entregador ainda não enviou GPS.' : 'This driver has not sent GPS yet.'}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriverLiveMap;
